// The payer's screens once the code is made, as steps: show friends the code, tap your own items,
// then the live board while everyone taps and pays. Then closing the split, then collecting.
import { joinLink } from '../../loop.ts';
import { PAYING_STALE_MS } from '../../pay-links.ts';
import { compute, fmt, nothingToPay, plain, type Totals } from '../../money.ts';
import { shareCard } from '../share-card.ts';
import { trackOnce, trackStep } from '../track.ts';
import { groupShare } from '../group-share.ts';
import { brand, titled } from '../../brand.ts';
import type { LeftoverMode, PublicPerson, PublicSplit } from '../../types.ts';
import { checkPhoneCode, payer as payerApi, poll, sendCode, session, type PayerAction } from '../api.ts';
import {
  app, logo, clampSize, closeSheet, copy, esc, frac, icon, initial, inputValue, leadCents, listNames,
  openSheet, privacyLink, safeRender, sheetBody, smsHref, stepBar, stepper, target, toast,
} from '../dom.ts';
import { onLeave } from '../router.ts';
import { saved, type Stage } from '../saved.ts';
import { allPicked, hostFoot, isLocked, lockFoot, lockIn, saidReady, unclaimedSentLine, type HostStep } from './lock.ts';
import { REFERRAL_LINE } from '../../fee-copy.ts';
import { fee } from '../fee.ts';
import { NAME_TOO_SHORT, nameLongEnough } from '../../names.ts';
import { errorScreen, knownVenue, missingCount, optInCard, optInClick, plural, surveyCard, surveyClick, tipPercent } from './shared.ts';

interface UI {
  split: PublicSplit | null;
  stage: Stage;
  closing: boolean;
  leftover: { mode: LeftoverMode; assignTo?: string };
  first: boolean;
}

/** A sheet that should redraw when fresh data arrives, so a friend who joins shows up in it. */
interface LiveSheet {
  box: HTMLElement;
  draw: () => void;
}

const prettyPhone = (n: string) => (/^\d{10}$/.test(n) ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : n);

export function payerView(code: string): void {
  const hashKey = new URLSearchParams(location.hash.slice(1)).get('k');
  if (hashKey) saved.setAdminKey(code, hashKey);
  const key = saved.adminKey(code);
  // A split made on this phone opens on the code. One opened later goes straight to the board.
  const ui: UI = { split: null, stage: saved.stage(code) ?? 'collect', closing: false, leftover: { mode: 'even' }, first: true };
  app.innerHTML = '<p class="loading">Loading…</p>';

  // The open item sheet follows the table: when a friend joins or claims while it's open, it
  // redraws (so "Shared?" turns on). Not while a tap is still saving, so a check doesn't flicker.
  let live: LiveSheet | null = null;
  let saving = 0;
  const keepLive = (draw: () => void) => {
    const box = sheetBody();
    live = box ? { box, draw } : null;
  };
  const redrawLiveSheet = () => {
    if (live && !saving && sheetBody() === live.box) live.draw();
  };

  const poller = poll(code, key, (s, err) => {
    if (err || !s) return errorScreen(err?.message || 'Something went wrong. Try again.');
    ui.split = s;
    if (ui.first) {
      ui.first = false;
      if (s.isAdmin) saved.rememberSplit(s);
    }
    // While an item tap is saving on "Tap what you had", wait for it, so the check doesn't flicker.
    if (!(saving && stageNow() === 'mine')) safeRender(render);
    redrawLiveSheet();
  });
  onLeave(poller.stop);
  // "Collect my N free splits": phone sign-in inline on the board's referral card. No sheet, no new
  // page. The number is prefilled only from this phone's own storage (what it gave as a friend);
  // the server never sends it.
  const collect = { step: 'idle' as 'idle' | 'phone' | 'code' | 'done', count: 0, phone: '', code: '', error: '', note: '', busy: false };
  const splitsWord = (n: number) => `${n} split${n === 1 ? '' : 's'} with no fee`;
  const ev = () => ({ code, count: collect.count });
  const collectClick = async (e: Event) => {
    const b = target<HTMLButtonElement>(e, '[data-act^=collect]');
    if (!b || collect.busy) return;
    const a = b.dataset.act;
    collect.error = ''; collect.note = '';
    if (a === 'collect') {
      collect.count = current()?.phoneCreditsWaiting || 0;
      collect.phone = collect.phone || saved.phone();
      collect.step = 'phone';
      trackStep('credits_collect_tapped', ev());
      return redraw('collectPhone');
    }
    collect.busy = true;
    try {
      if (a === 'collectSend' || a === 'collectResend') {
        await sendCode(collect.phone);
        trackStep('credits_code_sent', ev());
        collect.step = 'code';
        collect.code = '';
        if (a === 'collectResend') collect.note = 'We sent a new code.';
      } else if (a === 'collectCheck') {
        if (collect.code.replace(/\D/g, '').length !== 6) throw new Error('Enter the 6-digit code from the text.');
        const r = await checkPhoneCode(collect.phone, collect.code);
        session.set(r.session);
        collect.step = 'done';
        trackStep('credits_claimed', ev());
      } else if (a === 'collectBack') {
        collect.step = 'phone';
      }
    } catch (err) {
      // A wrong or expired code (the server said no): one clear line, with the resend right there.
      collect.error = a === 'collectCheck' && typeof (err as { status?: number }).status === 'number'
        ? "That code didn't work. Check the text, or tap Send a new code."
        : (err as Error).message;
    } finally { collect.busy = false; }
    redraw(collect.step === 'code' ? 'collectCode' : collect.step === 'phone' ? 'collectPhone' : '');
    if (collect.step === 'done') void poller.refresh();
  };
  const collectInput = (e: Event) => {
    const el = e.target as HTMLInputElement;
    if (el.id === 'collectPhone') collect.phone = el.value;
    if (el.id === 'collectCode') collect.code = el.value.replace(/\D/g, '').slice(0, 6);
  };
  app.addEventListener('click', collectClick);
  app.addEventListener('input', collectInput);
  // "Ask friends to pay", on the board and (if they closed first) the dashboard.
  const lockClick = async (e: Event) => {
    const b = target<HTMLButtonElement>(e, '[data-act="lock"]');
    if (!b || !key) return;
    if (await lockIn(code, key, b)) {
      // Sent: the board moves to "Done" now, not on the next refresh.
      if (ui.split) { ui.split.locked = undefined; ui.split.nudges = undefined; safeRender(render); }
      await poller.refresh();
    }
  };
  app.addEventListener('click', lockClick);
  onLeave(() => { app.removeEventListener('click', collectClick); app.removeEventListener('input', collectInput); app.removeEventListener('click', lockClick); });
  /** Redraw just the collect card, keeping focus where the next step is. */
  function redraw(focusId: string): void {
    const card = document.getElementById('collectCard');
    if (card) card.outerHTML = collectCard(current());
    if (focusId) (document.getElementById(focusId) as HTMLInputElement | null)?.focus();
  }
  function collectCard(s: PublicSplit | null): string {
    const n = collect.step === 'idle' ? s?.phoneCreditsWaiting || 0 : collect.count;
    if (!n) return '';
    const err = collect.error ? `<div class="error" id="collectError" role="alert">${esc(collect.error)}</div>` : '';
    const note = collect.note ? `<div class="note" id="collectNote" role="status">${esc(collect.note)}</div>` : '';
    let inner: string;
    if (collect.step === 'idle') {
      inner = `<span>Friends you brought earned you ${splitsWord(n)}.</span>
        <button class="btn" data-act="collect" id="collectBtn">Collect my ${splitsWord(n)}</button>`;
    } else if (collect.step === 'phone') {
      inner = `<label for="collectPhone" style="font-weight:600">Your phone number</label>
        <input id="collectPhone" class="input" type="tel" inputmode="tel" autocomplete="tel" value="${esc(collect.phone)}" placeholder="(303) 555-0123">
        <span class="hint">Use the number you gave as a friend. We'll text you a code. ${privacyLink()}</span>
        ${err}
        <button class="btn" data-act="collectSend">Text me a code</button>`;
    } else if (collect.step === 'code') {
      inner = `<label for="collectCode" style="font-weight:600">Enter the 6-digit code we texted you</label>
        <input id="collectCode" class="input amt" type="text" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]*" maxlength="6" value="${esc(collect.code)}" style="letter-spacing:6px;font-size:24px;text-align:center">
        ${err}${note}
        <button class="btn" data-act="collectCheck">Collect</button>
        <div style="display:flex;justify-content:space-between">
          <button class="link" data-act="collectResend">Send a new code</button>
          <button class="link" data-act="collectBack">Change number</button>
        </div>`;
    } else {
      inner = `<b id="collectDone">${splitsWord(n).replace(/^./, (c) => c.toUpperCase())} added. No subscription. Your next ${n === 1 ? 'split has no fee' : `${n} splits have no fee`}.</b>`;
    }
    return `<div class="card stack" id="collectCard" style="gap:10px">${inner}</div>`;
  }

  const dashLink = () => `${location.origin}/p/${code}?src=dash#k=${key}`;
  /** The friend link from Text it (`text`) or Copy link (`copy`): the same dinner, tagged by button. */
  const friendLink = (via: 'text' | 'copy' = 'copy') => location.origin + joinLink(code, ui.split?.payerRef, 'invite', via);
  const shareTapped = (channel: 'qr' | 'group' | 'text' | 'copy') => trackStep('share_tapped', { code, channel });
  const current = (): PublicSplit => ui.split as PublicSplit;

  async function act(a: PayerAction): Promise<void> {
    try {
      await payerApi(code, key || '', a);
      await poller.refresh();
    } catch (err) {
      toast((err as Error).message);
    }
  }

  /** The step to show. Splitting evenly has no items to tap, so it goes from the code to the board. */
  const stageNow = (): Stage => (ui.stage === 'mine' && ui.split?.even ? 'collect' : ui.stage);
  function setStage(stage: Stage): void {
    ui.stage = stage;
    saved.setStage(code, stage);
    window.scrollTo(0, 0);
    render();
  }

  function render(): void {
    const s = ui.split;
    if (!s) return;
    document.title = titled(s.venue);
    if (!s.isAdmin) return renderNotPayer(s);
    if (ui.closing) return renderClose(s);
    if (s.status !== 'open') return renderDashboard(s);
    const stage = stageNow();
    if (stage === 'share') return renderShare(s);
    if (stage === 'mine') return renderMine(s);
    return renderBoard(s);
  }

  function renderNotPayer(s: PublicSplit): void {
    app.innerHTML = `
      <div class="bar">${logo}<span>${esc(s.venue)}</span></div>
      <div class="body">
        <h1 class="h">This is ${esc(s.payer.name)}'s page.</h1>
        <p class="lede">If you're ${esc(s.payer.name)}, open the dashboard link you saved when you created the split. It has a private key in it.</p>
        <a class="btn" href="/s/${esc(code)}" data-go>I'm a friend. Show my share.</a>
      </div>`;
  }

  // ---------- step: show friends the code ----------

  /** The text that goes to the group chat. It comes from the payer, so it reads like them. */
  function inviteText(s: PublicSplit): string {
    const c = compute(s);
    const got = knownVenue(s) ? `I got the bill at ${s.venue}.` : 'I got the bill.';
    return c.even
      ? `${got} It's ${fmt(c.even.share)} each, tax and tip included. Pay me back here: ${friendLink('text')}`
      : `${got} Tap what you had and pay me back here: ${friendLink('text')}`;
  }

  /** "Paying" (tapped Venmo or Cash App, not confirmed). After 30 minutes it asks the payer to check. */
  function payingPill(p: PublicPerson): string {
    if (p.paid || !p.paying) return '';
    const appName = p.paying.app === 'cashapp' ? 'Cash App' : 'Venmo';
    return Date.now() - Date.parse(p.paying.at) >= PAYING_STALE_MS
      ? `<span class="pill owes" data-paying="stale">Paying? Check your ${appName}</span>`
      : `<span class="pill" data-paying="on">Paying</span>`;
  }

  /** The payer's referral line, with the code small and any banked free splits. */
  function referralCard(s: PublicSplit): string {
    // Pricing off: every split has no fee, so there's nothing to earn or collect. No card.
    if (!s.payerRef || !fee.pricing) return '';
    const banked = s.freeBanked || 0;
    return `
      <div class="card stack" id="referralCard" style="gap:4px">
        <span id="referralLine">${esc(REFERRAL_LINE)}</span>
        <span class="sub">Your code: ${esc(s.payerRef)}${banked ? ` · <b id="freeBanked">${banked} split${banked === 1 ? '' : 's'} with no fee banked</b>` : ''}</span>
      </div>
      ${collectCard(s)}`;
  }

  function renderShare(s: PublicSplit): void {
    const friends = s.people.filter((p) => !p.isPayer);
    const missing = missingCount(s);
    // One share screen for every table: the code, "Send to the group text", Text it and Copy link.
    const actions = `
        <div class="stack" style="gap:8px">
          <button class="btn2" data-act="groupShare">Send to the group text</button>
          <div class="row2">
            <a class="btn2" id="textIt" data-act="textIt" href="${smsHref('', inviteText(s))}">Text it</a>
            <button class="btn2" data-act="copyFriend">Copy link</button>
          </div>
          <span class="sub center">Send it to the group chat, or to anyone who already left.</span>
        </div>`;
    // The QR code on screen counts as the QR button, once per split on this phone.
    trackOnce('qrShown:' + code, 'share_tapped', { code, channel: 'qr' });
    app.innerHTML = `
      ${stepBar(`<span style="font-weight:600;color:var(--ink)">${esc(s.venue)}</span>`, s.even, 'Share')}
      <div class="body">
        <div class="stack" style="gap:6px">
          <h1 class="h">Have friends scan this</h1>
          <p class="lede">They point their phone camera at it. No app needed.</p>
        </div>
        <div class="qr-card">
          <img src="/qr/${esc(code)}.svg" alt="QR code that opens this split">
          <div class="qr-code">${esc(location.host)} · <b>${esc(code)}</b></div>
        </div>
        ${actions}
        <div class="card stack" style="gap:8px" aria-live="polite">
          <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px">
            <b>${s.partySize ? (missing ? `${s.people.length} of ${s.partySize} here` : `All ${s.partySize} here`) : `${s.people.length} here`}</b>
            <button class="link" data-act="size" style="padding:0">Change</button>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:8px">
            <span class="pill paid">You</span>
            ${friends.map((p) => `<span class="pill paid">${esc(p.name)}</span>`).join('')}
            ${Array.from({ length: Math.min(missing, 8) }, () => '<span class="pill off">?</span>').join('')}
          </div>
          <span class="sub">${s.partySize && !missing ? "Everyone's in. Tap Next." : 'Names pop up here as friends join.'}</span>
          <button class="link" data-act="addPerson" style="align-self:flex-start;padding:0">+ Add someone without a phone</button>
        </div>
      </div>
      <div class="foot">
        <button class="btn" data-act="next">${s.even ? 'Next' : 'Next: tap what you had'}</button>
        <span class="sub center">You can show the code again anytime.</span>
      </div>`;

    app.onclick = (e) => {
      const b = target(e, '[data-act]');
      if (!b) return;
      switch (b.dataset.act) {
        case 'copyFriend': shareTapped('copy'); void copy(friendLink('copy')); break;
        case 'textIt': shareTapped('text'); break; // the link itself opens Messages
        case 'groupShare': shareTapped('group'); void groupShare(current(), 'payer'); break;
        case 'size': sizeSheet(); break;
        case 'addPerson': addPersonSheet(); break;
        case 'next': setStage(current().even ? 'collect' : 'mine'); break;
      }
    };
  }

  // ---------- step: tap what you had ----------

  function renderMine(s: PublicSplit): void {
    const me = s.people.find((p) => p.isPayer);
    if (!me) return setStage('collect');
    const c = compute(s);
    app.innerHTML = `
      ${stepBar(`<button class="back" data-act="back">${icon.back}Code</button>`, s.even, 'Your items')}
      <div class="body">
        <div class="stack" style="gap:6px">
          <h1 class="h">Tap what you had</h1>
          <p class="lede">Shared something? Tap it, then tap Shared? and pick who had it with you.</p>
        </div>
        <div class="stack" style="gap:8px">${itemRows(s, me.id)}</div>
      </div>
      <div class="foot">
        <div style="display:flex;justify-content:space-between;align-items:baseline"><span class="muted" style="font-size:14px">Your share so far</span><span class="amt" style="font-size:22px;font-weight:700">${fmt(c.people[me.id].current)}</span></div>
        <button class="btn" data-act="next">Done</button>
      </div>`;

    app.onclick = async (e) => {
      if (await itemTap(e, me.id, () => render(), () => { closeSheet(); render(); })) return;
      const b = target(e, '[data-act]');
      if (b?.dataset.act === 'back') setStage('share');
      else if (b?.dataset.act === 'next') setStage('collect');
    };
  }

  // ---------- step: the live board while everyone taps and pays ----------

  function renderBoard(s: PublicSplit): void {
    const c = compute(s);
    const friends = s.people.filter((p) => !p.isPayer);
    const missing = missingCount(s);
    const rows = s.people.map((p) => {
      const r = c.people[p.id];
      const items = r.items.map((i) => (i.ways > 1 ? `${frac(i.ways)} ${i.name}` : i.name));
      return { p, r, itemsText: items.length ? items.join(', ') : isLocked(s) ? 'Nothing to pay' : saidReady(s, p.id) ? 'Had nothing' : 'Nothing claimed yet' };
    });
    const me = s.people.find((p) => p.isPayer);
    const iPicked = Boolean(me && c.people[me.id].items.length);
    // One green button at a time: your items, then Ask friends to pay, then Done.
    const step: HostStep = isLocked(s) ? 'done' : c.even || iPicked || !me ? 'send' : 'pick';
    const sent = step === 'done';
    // Everyone's in and picked: the number is final until someone changes a pick, so no "so far".
    const ready = !sent && (c.even ? !missing : allPicked(s));
    app.innerHTML = `
      ${stepBar(`<span style="font-weight:600;color:var(--ink)">${esc(s.venue)}</span>`, s.even, 'Get paid')}
      <div class="body pad-foot">
        <h1 class="h">${sent ? 'Totals sent' : step === 'pick' ? 'Pick your items first' : c.even ? (missing ? 'Wait for everyone to join' : "Everyone's in") : allPicked(s) ? "Everyone's picked" : c.unclaimed.items.length ? 'Wait for everyone to tap' : 'Everything is claimed'}</h1>
        <div class="hero" id="comingBack">
          <span class="lbl">Coming back to you${sent || ready ? '' : ' so far'}</span>
          <span class="big-amt" style="font-size:40px">${fmt(c.payer.comingBack)}</span>
          <span class="sub">${friends.length ? `From ${plural(friends.length, 'friend')}${c.payer.breakdown.thanks ? `, including ${fmt(c.payer.breakdown.thanks)} thanks` : ''}.${sent || ready ? '' : ` Grows as people ${c.even ? 'join' : 'claim'}.`}` : "Nobody's joined yet. Show them the code."}</span>
        </div>
        ${c.unclaimed.items.length ? `
          <div class="alert" id="unclaimed">${icon.info}<span style="flex:1">${sent ? esc(unclaimedSentLine(c.unclaimed.items.map((i) => i.name))) : `<b>${plural(c.unclaimed.items.length, 'item')} unclaimed</b> · ${esc(c.unclaimed.items.map((i) => i.name).join(', '))}`}</span><span class="amt" style="font-weight:700">${fmt(c.unclaimed.total)}</span></div>` : ''}
        <div>
          <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px">
            <div class="lbl">${c.even ? `Table · ${fmt(c.even.share)} each` : sent ? 'Table' : 'Table · tap anyone to pick their items'}</div>
            <button class="link" data-act="more" id="moreBtn" aria-haspopup="dialog" style="padding:4px 0">More</button>
          </div>
          ${rows.map(({ p, r, itemsText }) => c.even ? `
            <div class="person">
              <span class="av ${p.isPayer ? 'payer' : ''}">${esc(initial(p.name))}</span>
              <span class="who"><span style="display:block;font-weight:600">${p.isPayer ? 'You' : esc(p.name)}${p.addedByPayer ? ' <span class="sub">· added by you</span>' : ''}</span><span class="sub" style="display:block">${p.isPayer ? 'Your share' : 'Even share'}</span></span>
              ${r.paid ? '<span class="pill settled">Says paid</span>' : payingPill(p)}
              <span class="amt" style="font-weight:600;min-width:64px;text-align:right">${fmt(p.isPayer ? r.current : r.owed)}</span>
            </div>` : `
            <button class="person person-btn" data-act="claimFor" data-id="${esc(p.id)}">
              <span class="av ${p.isPayer ? 'payer' : ''}">${esc(initial(p.name))}</span>
              <span class="who"><span style="display:block;font-weight:600">${p.isPayer ? 'You' : esc(p.name)}${p.addedByPayer ? ' <span class="sub">· added by you</span>' : ''}</span><span class="sub" style="display:block">${esc(itemsText)}</span></span>
              ${r.paid ? '<span class="pill settled">Says paid</span>' : payingPill(p)}
              <span class="amt" style="font-weight:600;min-width:64px;text-align:right">${fmt(p.isPayer ? r.current : r.owed)}</span>
            </button>`).join('')}
          ${Array.from({ length: Math.min(missing, 8) }, () => `
            <div class="person">
              <span class="av ghost">?</span>
              <span class="who sub">Hasn't joined yet</span>
            </div>`).join('')}
        </div>
        ${c.even ? evenCard(c.even) : ''}
        ${slipCard(s, c)}
        ${sent ? referralCard(s) : ''}
        ${sent && saved.isFresh(code) ? `
          <div class="card stack" id="dashCard">
            <div style="font-weight:600">Save your dashboard link</div>
            <p class="sub" style="margin:0">It's how you get back here to see who paid and send reminders, from any phone. There's no account.</p>
            <button class="btn2" data-act="copyDash">Copy link</button>
          </div>` : ''}
      </div>
      <div class="foot">${hostFoot(s, step)}</div>`;

    app.onclick = (e) => {
      const b = target(e, '[data-act]');
      if (!b) return;
      switch (b.dataset.act) {
        case 'copyFriend': shareTapped('copy'); void copy(friendLink('copy')); break;
        // Copying it is the done: the card goes once the link is saved.
        case 'copyDash': void copy(dashLink()).then(() => { saved.setFresh(code, false); render(); }); break;
        case 'showQR': setStage('share'); break;
        case 'close': ui.closing = true; ui.leftover = { mode: 'even' }; window.scrollTo(0, 0); render(); break;
        case 'size': sizeSheet(); break;
        case 'evenOn': sizeSheet(true); break;
        case 'evenOff': void act({ action: 'setEven', on: false }); break;
        case 'addPerson': addPersonSheet(); break;
        case 'claimFor': if (b.dataset.id) claimForSheet(b.dataset.id); break;
        case 'mine': mineSheet(); break;
        case 'more': moreSheet(); break;
      }
    };
  }

  /** Everything the host might need now and then, behind one small "More" link. */
  function moreSheet(): void {
    const s = current();
    openSheet(`
      <h2 class="h" style="font-size:24px">More</h2>
      <div class="stack" style="gap:8px" id="moreList">
        <button class="btn2" data-act="size">${s.partySize ? `Change table size · ${s.partySize}` : 'How many at the table?'}</button>
        <button class="btn2" data-act="addPerson">Add someone without a phone</button>
        ${s.even ? '<button class="btn2" data-act="evenOff">Let everyone tap their items</button>' : '<button class="btn2" data-act="evenOn">Split it evenly instead</button>'}
        <button class="btn2" data-act="showQR">Show the code again</button>
      </div>
      <button class="link" data-close>Close</button>`,
    (e) => {
      const b = target(e, '[data-act]');
      if (!b) return;
      closeSheet();
      switch (b.dataset.act) {
        case 'size': sizeSheet(); break;
        case 'addPerson': addPersonSheet(); break;
        case 'evenOn': sizeSheet(true); break;
        case 'evenOff': void act({ action: 'setEven', on: false }); break;
        case 'showQR': setStage('share'); break;
      }
    });
  }

  /** The tip and total the payer writes on the card slip, so nobody does math at the table. */
  function slipCard(s: PublicSplit, c: ReturnType<typeof compute>): string {
    // A service charge is already printed on the bill; the review screen shows what to write then.
    if (/service charge/i.test(s.tipLabel)) return '';
    return `
      <div class="card" style="border:2px solid var(--accent);padding:12px 16px">
        <div class="lbl" style="padding-bottom:4px">On the card slip</div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><span style="font-weight:600">Tip</span><span class="amt" style="font-size:22px;font-weight:700">${fmt(c.tip)}</span></div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><span style="font-weight:600">Total</span><span class="amt" style="font-size:22px;font-weight:700">${fmt(c.total)}</span></div>
      </div>`;
  }

  /** Splitting evenly: one share for everyone, tax and tip included. Nobody taps items. */
  function evenCard(even: { ways: number; share: number }): string {
    return `
      <div class="card stack" style="border-color:var(--accent)">
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px">
          <span style="font-weight:600;font-size:18px">Split evenly, ${even.ways} ways</span>
          <span class="amt" style="font-weight:700;font-size:20px">${fmt(even.share)} each</span>
        </div>
        <p class="sub" style="margin:0">Tax and tip included. Any odd cents are on you. Change it under More.</p>
      </div>`;
  }

  function mineSheet(): void {
    const me = current().people.find((p) => p.isPayer);
    if (me) claimForSheet(me.id);
  }

  /** How many at the table. With `toEven`, saving also switches the split to even. */
  function sizeSheet(toEven = false): void {
    const atLeast = Math.max(2, current().people.length);
    let n = Math.max(current().partySize || 0, atLeast);
    const even = toEven || current().even;
    const draw = () => {
      const box = sheetBody();
      if (!box) return;
      const count = current().people.length;
      box.innerHTML = `
        <div class="grab"></div>
        <h2 class="h" style="font-size:26px">${even ? 'Split it how many ways?' : 'How many at the table?'}</h2>
        <p class="lede" style="font-size:15px">${even
          ? `Counting you. Everyone pays the same share of the bill, tax and tip included: ${fmt(Math.floor(compute(current()).total / n))} each.`
          : `Counting you. ${count} ${count === 1 ? 'is' : 'are'} on the list so far, so you'll see who's still missing.`}</p>
        <div style="display:flex;justify-content:center">${stepper(n, atLeast)}</div>
        <button class="btn" data-act="saveSize">${toEven ? 'Split evenly' : 'Save'}</button>
        <button class="link" data-close>Cancel</button>`;
    };
    openSheet('', async (e) => {
      const b = target(e, '[data-act]');
      if (!b) return;
      if (b.dataset.act === 'size') { n = Math.max(atLeast, clampSize(n + Number(b.dataset.d))); draw(); }
      else if (b.dataset.act === 'saveSize') { closeSheet(); await act(toEven ? { action: 'setEven', on: true, size: n } : { action: 'setPartySize', size: n }); }
    });
    draw();
  }

  function addPersonSheet(): void {
    const box = openSheet(`
      <h2 class="h" style="font-size:26px">Add someone without a phone</h2>
      <p class="lede" style="font-size:15px">${current().even ? "For a friend who isn't on their phone. You'll keep track of their share for them." : "For a friend who isn't on their phone. You'll tap their items for them."}</p>
      <form id="addP" class="stack" autocomplete="off">
        <input id="addName" class="input" placeholder="Their name" aria-label="Their name" maxlength="40">
        <input id="addPhone" class="input" type="tel" inputmode="tel" placeholder="Their phone (optional, for reminders)" aria-label="Their phone, optional">
        <span class="hint">Only you see it. We never sell it. ${privacyLink()}</span>
        <button class="btn">${current().even ? 'Add them' : 'Add and pick their items'}</button>
      </form>
      <button class="link" data-close>Cancel</button>`);
    box.querySelector('#addP')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = inputValue('addName');
      if (!name) return;
      if (!nameLongEnough(name)) { toast(NAME_TOO_SHORT); return; }
      const phone = inputValue('addPhone');
      const before = new Set(current().people.map((p) => p.id));
      closeSheet();
      await act({ action: 'addPerson', name, phone });
      const added = current().people.find((p) => !before.has(p.id));
      if (added && current().status === 'open' && !current().even) claimForSheet(added.id);
    });
  }

  /** One row per item, ticked if this person has it, with "Shared?" on their items. */
  function itemRows(s: PublicSplit, pid: string): string {
    const byId = new Map(s.people.map((x) => [x.id, x]));
    return s.items.map((it) => {
      const ids = (s.claims[it.id] || []).map((x) => x.pid).filter((id) => byId.has(id));
      const on = ids.includes(pid);
      const others = ids.filter((id) => id !== pid).map((id) => (byId.get(id)?.isPayer ? 'you' : byId.get(id)?.name || ''));
      // "Also Ava" only when this person has it too (it's shared). Someone else's item is just "Ava",
      // like the friend's picker.
      // Always there on your items, so you know it exists. Greyed out until someone else joins.
      const shareBtn = !on ? ''
        : s.people.length > 1 ? `<button class="shared-btn" data-share="${esc(it.id)}">Shared?</button>`
        : `<button class="shared-btn" aria-disabled="true" data-share-wait>Shared?</button>`;
      return `
        <div style="display:flex;gap:8px;align-items:stretch">
          <button class="check" role="checkbox" aria-checked="${on}" data-item="${esc(it.id)}" style="flex:1;min-width:0">
            <span class="box">${on ? icon.check : ''}</span>
            <span style="flex:1;min-width:0">${esc(it.name)}<span class="sub" style="display:block;font-weight:400">${others.length ? (on ? 'Also ' : '') + esc(listNames(others)) : on ? '' : 'Not claimed yet'}</span></span>
            <span class="amt" style="font-weight:500">${plain(it.cents)}</span>
          </button>
          ${shareBtn}
        </div>`;
    }).join('');
  }

  /**
   * A tap on an item row: tick or untick it, or open "Shared?". True when the tap was handled.
   * `redraw` runs once the tick is saved; `afterShared` once "Who shared" is done.
   */
  async function itemTap(e: Event, pid: string, redraw: () => void, afterShared: () => void): Promise<boolean> {
    const sh = target(e, '[data-share]');
    if (sh?.dataset.share) { whoSharedSheet(pid, sh.dataset.share, afterShared); return true; }
    if (target(e, '[data-share-wait]')) { toast('Once a friend joins, you can split this with them.'); return true; }
    const b = target(e, '[data-item]');
    if (!b?.dataset.item) return false;
    const on = b.getAttribute('aria-checked') !== 'true';
    b.setAttribute('aria-checked', String(on));
    const box = b.querySelector('.box');
    if (box) box.innerHTML = on ? icon.check : '';
    // Show it on the board right away (the host's own picks too), then sync with the server.
    const itemId = b.dataset.item;
    const s0 = current();
    const payerId = s0.people.find((x) => x.isPayer)?.id || pid;
    const list = (s0.claims[itemId] || []).filter((x) => x.pid !== pid);
    if (on) list.push({ pid, by: payerId });
    s0.claims = { ...s0.claims, [itemId]: list };
    if (stageNow() !== 'mine') safeRender(render);
    saving++;
    try {
      await act({ action: 'claimFor', personId: pid, itemId, on });
    } finally {
      saving--;
    }
    // The refresh above may have been held back while this tap was saving: draw what came back.
    if (stageNow() !== 'mine') safeRender(render);
    redraw();
    return true;
  }

  function claimForSheet(pid: string): void {
    const draw = () => {
      const box = sheetBody();
      const s = current();
      const p = s.people.find((x) => x.id === pid);
      if (!box || !p) return closeSheet();
      const c = compute(s);
      box.innerHTML = `
        <div class="grab"></div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px">
          <h2 class="h" style="font-size:24px">${p.isPayer ? 'Your items' : `Items for ${esc(p.name)}`}</h2>
          <span class="amt" style="font-weight:700">${fmt(c.people[pid].current)}</span>
        </div>
        <p class="sub" style="margin:0">${p.isPayer ? 'Tap what you had. If you shared something, tap it too and it splits evenly with whoever else taps it.' : `Tap what ${esc(p.name)} had. Shared items split evenly. ${esc(p.name)} sees these as marked by you and can remove them.`}</p>
        ${itemRows(s, pid)}
        ${!p.isPayer && !p.paid ? `<button class="btn2" data-act="remindP">Send ${esc(p.name)} the link</button>` : ''}
        <button class="btn" data-close>Done</button>`;
    };
    openSheet('', async (e) => {
      if (await itemTap(e, pid, () => { if (sheetBody()) draw(); }, () => claimForSheet(pid))) return;
      if (target(e, '[data-act=remindP]')) {
        const p = current().people.find((x) => x.id === pid);
        if (p) remindSheet(current(), compute(current()), p);
      }
    });
    draw();
    keepLive(draw);
  }

  /** Pick who else had a share of an item. Same as a friend's "Shared?", done by the payer for anyone. */
  function whoSharedSheet(pid: string, itemId: string, after: () => void): void {
    const s0 = current();
    const it = s0.items.find((x) => x.id === itemId);
    const owner = s0.people.find((x) => x.id === pid);
    if (!it || !owner) return;
    const before = new Set((s0.claims[itemId] || []).map((x) => x.pid).filter((id) => id !== pid));
    const sel = new Set(before);
    // Everyone else at the table right now, so a friend who joins while this is open shows up.
    const others = () => current().people.filter((x) => x.id !== pid);
    const draw = () => {
      const box = sheetBody();
      if (!box) return;
      const n = 1 + sel.size;
      box.innerHTML = `
        <div class="grab"></div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><h2 class="h" style="font-size:24px">Who shared the ${esc(it.name)}?</h2><span class="amt">${plain(it.cents)}</span></div>
        <button class="check" aria-checked="true" disabled><span class="box">${icon.check}</span>${owner.isPayer ? 'You' : esc(owner.name)}</button>
        ${others().map((x) => `<button class="check" role="checkbox" aria-checked="${sel.has(x.id)}" data-pid="${esc(x.id)}"><span class="box">${sel.has(x.id) ? icon.check : ''}</span>${x.isPayer ? 'You' : esc(x.name)}</button>`).join('')}
        <div style="display:flex;justify-content:space-between;align-items:baseline"><b>${n === 1 ? (owner.isPayer ? 'Just you' : `Just ${esc(owner.name)}`) : `Split ${n} ways`}</b><span class="amt" style="font-weight:700;font-size:18px">${fmt(Math.round(it.cents / n))} each</span></div>
        <p class="sub" style="margin:0">The people you pick see it on their own page, marked by you, and can remove it.</p>
        <button class="btn" data-act="shareDone">Done</button>`;
    };
    openSheet('', async (e) => {
      const pick = target(e, '[data-pid]');
      if (pick?.dataset.pid) {
        const id = pick.dataset.pid;
        if (sel.has(id)) sel.delete(id); else sel.add(id);
        return draw();
      }
      if (target(e, '[data-act=shareDone]')) {
        for (const id of others().map((x) => x.id)) {
          if (sel.has(id) !== before.has(id)) await act({ action: 'claimFor', personId: id, itemId, on: sel.has(id) });
        }
        after();
      }
    });
    draw();
    keepLive(draw);
  }

  // ---------- closing ----------

  function renderClose(s: PublicSplit): void {
    const c = compute(s);
    const lo = ui.leftover;
    const friends = s.people.filter((p) => !p.isPayer);
    const sim = compute({ ...s, status: 'closed', leftover: lo });
    const hasLeft = c.unclaimed.items.length > 0;
    const missing = missingCount(s);
    // People who already paid whose share changes on closing: they'll see the difference and settle it.
    const changes = friends
      .map((p) => ({ p, up: sim.people[p.id].due, down: sim.people[p.id].refund }))
      .filter((x) => sim.people[x.p.id].paid && (x.up > 0 || x.down > 0));
    const opts: Array<{ id: LeftoverMode; title: string; sub: string }> = [
      { id: 'even', title: 'Split evenly', sub: `Everyone at the table takes an equal share, about ${fmt(Math.round(c.unclaimed.total / s.people.length))} each` },
      { id: 'assign', title: 'Give to someone', sub: 'Pick who had them' },
      { id: 'cover', title: "I'll cover it", sub: `You keep the ${fmt(c.unclaimed.total)}` },
    ];
    const needsPick = lo.mode === 'assign' && !lo.assignTo && hasLeft;
    const me = s.people.find((p) => p.isPayer);
    const payerHasNothing = Boolean(me && !c.people[me.id].items.length);
    app.innerHTML = `
      <div class="bar"><button class="back" data-act="cancel">${icon.back}Board</button><span>${esc(s.venue)}</span></div>
      <div class="body pad-foot">
        <h1 class="h">${c.even ? `Split evenly, ${c.even.ways} ways` : hasLeft ? 'Nobody claimed these' : 'Everything is claimed'}</h1>
        ${payerHasNothing && hasLeft ? `
          <div class="alert" style="flex-direction:column;align-items:stretch;gap:10px">
            <span>You haven't picked your own items. If some of these are yours, pick them first so they aren't split with everyone.</span>
            <button class="small-btn" data-act="mine" style="align-self:flex-start">Pick my items</button>
          </div>` : ''}
        ${missing ? `
          <div class="alert" style="flex-direction:column;align-items:stretch;gap:10px">
            <span>${c.even
              ? `${missing} of the ${s.partySize} people at the table never joined. Add them so you can send them a reminder. If you close anyway, their ${fmt(c.unclaimed.total)} stays on you.`
              : `${missing} of the ${s.partySize} people at the table never joined. Add them so their items are on them and you can send them a reminder. Or close anyway and the leftovers are handled below.`}</span>
            <button class="small-btn" data-act="addPerson" style="align-self:flex-start">Add someone without a phone</button>
          </div>` : ''}
        ${hasLeft ? `
          <div>
            ${c.unclaimed.items.map((i) => `<div class="person" style="padding:8px 0"><div class="who">${esc(i.name)}</div><span class="amt">${plain(i.cents)}</span></div>`).join('')}
            <div class="person" style="border:0;padding:8px 0"><div class="who sub">With ${s.taxLabel ? 'tax, fees' : 'tax'} and tip</div><span class="amt" style="font-weight:700">${fmt(c.unclaimed.total)}</span></div>
          </div>
          <div class="stack" role="radiogroup" aria-label="What to do with unclaimed items">
            ${opts.map((o) => `<button class="opt" role="radio" aria-checked="${lo.mode === o.id}" data-act="mode" data-mode="${o.id}"><span class="ring"></span><span style="flex:1"><b>${o.title}</b><span class="sub">${o.sub}</span></span></button>`).join('')}
          </div>
          ${lo.mode === 'assign' ? `
            <div class="field"><label for="assignTo">Give them to</label>
              <select id="assignTo" class="input">
                <option value="">Choose a person</option>
                ${s.people.map((p) => `<option value="${esc(p.id)}" ${lo.assignTo === p.id ? 'selected' : ''}>${p.isPayer ? 'Me' : esc(p.name)}</option>`).join('')}
              </select>
            </div>
            <form class="row2" id="addForm"><input class="input" id="newPerson" placeholder="Someone who didn't join" aria-label="Name of someone who didn't join"><button class="btn2" style="flex:0 0 80px">Add</button></form>` : ''}
          ${changes.map((x) => `<div class="note">${esc(x.p.name)} already paid. ${x.up ? `Their share goes up ${fmt(x.up)}; they'll see it with a one-tap button to pay the rest.` : `Their share goes down ${fmt(x.down)}; you'll see it to pay back.`}</div>`).join('')}
        ` : `<p class="lede">${c.even ? `Everyone pays ${fmt(c.even.share)}. Closing locks the amounts.` : 'Every item has someone on it. Closing locks the amounts so everyone knows exactly what they owe.'}</p>`}
      </div>
      <div class="foot">
        <div style="display:flex;justify-content:space-between;align-items:baseline"><span class="muted" style="font-size:14px">Coming back to you</span><span class="amt" style="font-size:22px;font-weight:700">${needsPick ? '—' : fmt(sim.payer.comingBack)}</span></div>
        <button class="btn" data-act="doClose" ${needsPick ? 'disabled' : ''}>Close split</button>
        <div class="sub center">Amounts become final for anyone who hasn't paid. You can reopen it later.</div>
      </div>`;

    app.onclick = async (e) => {
      const b = target<HTMLButtonElement>(e, '[data-act]');
      if (!b) return;
      switch (b.dataset.act) {
        case 'cancel': ui.closing = false; render(); break;
        case 'addPerson': addPersonSheet(); break;
        case 'mine': mineSheet(); break;
        case 'mode': ui.leftover = { mode: (b.dataset.mode as LeftoverMode) || 'even' }; render(); break;
        case 'doClose':
          b.disabled = true;
          await act({ action: 'close', leftover: ui.leftover });
          ui.closing = false;
          window.scrollTo(0, 0);
          render();
          break;
      }
    };
    app.onchange = (e) => {
      if (e.target instanceof HTMLSelectElement && e.target.id === 'assignTo') {
        ui.leftover = { mode: 'assign', assignTo: e.target.value || undefined };
        render();
      }
    };
    app.onsubmit = async (e) => {
      e.preventDefault();
      const input = document.getElementById('newPerson') as HTMLInputElement | null;
      const name = input?.value.trim();
      if (!input || !name) return;
      if (!nameLongEnough(name)) { toast(NAME_TOO_SHORT); return; }
      input.value = '';
      input.blur();
      const before = new Set(current().people.map((p) => p.id));
      await act({ action: 'addPerson', name });
      const added = current().people.find((p) => !before.has(p.id));
      if (added) ui.leftover = { mode: 'assign', assignTo: added.id };
      render();
    };
  }

  // ---------- closed: collecting ----------

  function renderDashboard(s: PublicSplit): void {
    const c = compute(s);
    const P = c.payer;
    const friends = s.people.filter((p) => !p.isPayer);
    const bd = P.breakdown;
    const pct = P.comingBack > 0 ? Math.round((P.received / P.comingBack) * 100) : 0;
    const friendsLabel = plural(friends.length, 'friend');
    // The "Post tonight" picture is offered once everyone has paid back.
    if (P.settled && friends.length) trackOnce('cardoffer:' + s.code, 'share_card_offered', { code: s.code });
    const ledger = `
      <div class="ledger">
        <div class="lbl" style="padding-bottom:4px">Coming back to you</div>
        ${leadCents('Their food &amp; drinks', bd.sub)}
        ${leadCents(`Their ${s.taxLabel ? 'tax & fees' : 'tax'}`, bd.tax)}
        ${leadCents('Their tip', bd.tip)}
        ${bd.thanks ? leadCents(`Thanks for covering · ${friends.filter((f) => c.people[f.id].thanks).length} × ${fmt(s.thanksCents)}`, bd.thanks, 'accent') : ''}
        ${bd.fee ? leadCents(`Their part of the ${esc(brand.name)} fee`, bd.fee, 'accent') : ''}
        ${leadCents(`From ${friendsLabel}`, P.comingBack, 'total')}
      </div>`;
    const me = s.people.find((p) => p.isPayer);
    const mine = me ? c.people[me.id] : null;
    const taxWord = s.taxLabel ? 'tax & fees' : 'tax';
    // Each friend's share of the bill itself (no thanks), so your share + theirs is exactly the bill.
    // How the bill balances: each friend's share and yours add up to exactly what the restaurant
    // charged, column by column. The thanks is on top of the bill, so it's listed under it.
    const part = (ids: string[]) => ids.reduce((a, id) => {
      const r = c.people[id];
      return { food: a.food + r.sub, tax: a.tax + r.tax, tip: a.tip + r.tip };
    }, { food: 0, tax: 0, tip: 0 });
    const theirs = part(friends.map((f) => f.id));
    const yours = mine ? { food: mine.sub, tax: mine.tax, tip: mine.tip } : { food: 0, tax: 0, tip: 0 };
    const bill = { food: c.subtotal, tax: c.tax, tip: c.tip };
    const sum = (x: { food: number; tax: number; tip: number }) => x.food + x.tax + x.tip;
    const balances = ['food', 'tax', 'tip'].every((k) => theirs[k as 'food'] + yours[k as 'food'] === bill[k as 'food']);
    const n = (cents: number) => plain(cents).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const row = (label: string, x: { food: number; tax: number; tip: number }, strong = false) => `
      <tr${strong ? ' style="font-weight:700;border-top:1.5px solid var(--ink)"' : ''}>
        <th scope="row" style="text-align:left;font-weight:${strong ? 700 : 500};padding:6px 6px 6px 0">${label}</th>
        <td>${n(x.food)}</td><td>${n(x.tax)}</td><td>${n(x.tip)}</td><td style="font-weight:700">${n(sum(x))}</td>
      </tr>`;
    const yourShare = mine ? `
      <div class="ledger">
        <div class="lbl" style="padding-bottom:6px">How the bill balances</div>
        <table class="amt" style="width:100%;border-collapse:collapse;font-size:13px;font-variant-numeric:tabular-nums">
          <thead><tr style="color:var(--ink-3)"><th></th><th style="text-align:right;font-weight:500">Food</th><th style="text-align:right;font-weight:500">${s.taxLabel ? 'Tax&nbsp;&amp;<br>fees' : 'Tax'}</th><th style="text-align:right;font-weight:500">Tip</th><th style="text-align:right;font-weight:500">Total</th></tr></thead>
          <tbody style="text-align:right">
            ${row(`Friends (${friends.length})`, theirs)}
            ${row('You', yours)}
            ${row('= Bill', bill, true)}
          </tbody>
        </table>
        <p class="sub" style="margin:8px 0 0">${balances
          ? `✓ Friends ${fmt(sum(theirs))} + you ${fmt(sum(yours))} = ${fmt(c.total)}, what you paid${knownVenue(s) ? ` ${esc(s.venue)}` : ''}. It balances to the cent.`
          : `Off by ${fmt(Math.abs(sum(theirs) + sum(yours) - c.total))}. Something changed after people paid.`}</p>
        ${s.fees?.length ? `<p class="sub" style="margin:4px 0 0">Tax &amp; fees includes ${s.fees.map((f) => `${esc(f.name)} ${fmt(f.cents)}`).join(', ')}.</p>` : ''}
        <p class="sub" style="margin:4px 0 0">Tip: ${!s.tipLabel || s.tipLabel === 'Tip' ? `${tipPercent(s)}% of the food` : s.tipLabel.endsWith('%') ? `${esc(s.tipLabel)} of the food` : esc(s.tipLabel)}.${bd.thanks ? ` Friends also send ${fmt(bd.thanks)} in thanks on top of the bill, yours to keep.` : ''}</p>
      </div>` : '';

    const status = (p: PublicPerson) => {
      const r = c.people[p.id];
      if (r.forgiven && !r.paid) return { pill: '<span class="pill off">Let go</span>', line: fmt(r.owed), owes: false, back: false };
      // Their share is $0: nothing to pay, so no Owes and no Remind. Worked out from the share, never marked.
      if (nothingToPay(r)) return { pill: '', line: 'Nothing to pay', owes: false, back: false, none: true };
      if (r.paid && r.due > 0) return { pill: '<span class="pill owes">Owes</span>', line: `${fmt(r.due)} more · their share went up after they paid ${fmt(r.paid.cents)}`, owes: true, back: false };
      if (r.paid && r.refund > 0) return { pill: '<span class="pill settled">Paid</span>', line: `${fmt(r.paid.cents)} · their share went down, you owe them ${fmt(r.refund)} back`, owes: false, back: true };
      if (r.paid) return { pill: '<span class="pill settled">Paid</span>', line: `${fmt(r.paid.cents)} · ${r.paid.how === 'payer' ? 'marked by you' : 'marked paid'}`, owes: false, back: false };
      return { pill: '<span class="pill owes">Owes</span>', line: fmt(r.owed), owes: true, back: false };
    };

    app.innerHTML = `
      <div class="bar">${logo}<span>${esc(s.venue)} · ${P.settled ? 'settled' : 'closed'}</span></div>
      <div class="body">
        ${isLocked(s) ? '' : `
          <div class="card stack" id="lockCard" style="gap:6px">
            <div style="font-weight:600">Friends can't pay yet</div>
            <p class="sub" style="margin:0">Once you ask, they see what they owe and can pay you.</p>
            <div class="error" id="lockError" role="alert" hidden></div>
            ${lockFoot(s)}
          </div>`}
        ${P.settled ? `
          <div class="done-mark settled">${icon.bigCheck}</div>
          <div><h1 class="h h-xl settled">Settled.</h1><p class="lede" style="margin-top:8px">Everyone's square. The group chat can go back to memes.</p></div>
          <div class="ledger">
            <div class="lbl" style="padding-bottom:6px">The night, tallied</div>
            ${leadCents(knownVenue(s) ? `You paid ${esc(s.venue)}` : 'You paid the bill', c.total)}
            ${leadCents(`Back from ${friendsLabel}`, -(P.comingBack - P.forgiven))}
            ${leadCents('Your part cost you', P.cost, 'total')}
          </div>` : `
          <div>
            <span class="lbl">Still out</span>
            <div class="big-amt">${fmt(P.outstanding)}</div>
            <div class="progress"><span style="width:${pct}%"></span></div>
            <div class="sub" style="display:flex;justify-content:space-between"><span>${fmt(P.received)} received · ${P.paidCount} of ${P.friendCount}</span><span>of ${fmt(P.comingBack)}</span></div>
          </div>`}
        ${P.settled && friends.length ? `
          <div class="next-card">
            <div class="h">Post tonight</div>
            <p>A picture of the split for your story or group chat, with your code on it. No names on it.</p>
            <button data-act="shareCard">Share a picture</button>
          </div>` : ''}
        ${slipCard(s, c)}
        ${ledger}
        ${yourShare}
        <div>
          <div class="lbl">Who's paid</div>
          ${friends.length ? friends.map((p) => {
            const st = status(p);
            return `
              <div class="person">
                <button class="who" data-act="person" data-id="${esc(p.id)}" style="background:none;border:0;padding:0;text-align:left;cursor:pointer">
                  <div>${esc(p.name)}</div><div class="sub amt">${st.line}</div>
                </button>
                ${st.owes && !p.paid ? payingPill(p) || st.pill : st.pill}
                ${'none' in st ? '' : st.owes ? `<button class="small-btn" data-act="remind" data-id="${esc(p.id)}">Remind</button>` : st.back ? `<button class="ghost-btn" data-act="paidBack" data-id="${esc(p.id)}">Paid back</button>` : `<button class="ghost-btn" data-act="person" data-id="${esc(p.id)}">Change</button>`}
              </div>`;
          }).join('') : '<p class="sub">Nobody joined this split.</p>'}
          <p class="sub">Got paid another way? Tap a name to mark it paid.</p>
        </div>
        ${referralCard(s)}
        ${surveyCard(s)}
        ${optInCard(s)}
        <div class="row2"><button class="btn2" data-act="copyDash">Copy my dashboard link</button></div>
        <button class="link" data-act="reopen">Reopen for claims</button>
      </div>`;

    app.onclick = async (e) => {
      if (await optInClick(e, s, saved.membership(code)?.token, () => renderDashboard(current()))) return;
      if (await surveyClick(e, s, saved.membership(code)?.token, () => renderDashboard(current()))) return;
      const b = target(e, '[data-act]');
      if (!b) return;
      const p = s.people.find((x) => x.id === b.dataset.id);
      switch (b.dataset.act) {
        case 'copyDash': void copy(dashLink()); break;
        case 'shareCard': {
          b.setAttribute('disabled', '');
          const last = Math.max(...friends.map((f) => (f.paid ? Date.parse(f.paid.at) : 0)));
          const facts = {
            venue: s.venue, friends: friends.length, paidCount: P.paidCount, totalCents: c.total,
            mins: P.settled && last > 0 ? (last - Date.parse(s.createdAt)) / 60_000 : null, code: s.payerRef,
          };
          const link = `${location.origin}/?${s.payerRef ? `ref=${encodeURIComponent(s.payerRef)}&` : ''}utm_source=share_card&src=share_card`;
          try {
            const how = await shareCard(facts, link);
            if (how !== 'closed') trackStep('card_shared', { code: s.code, how });
            if (how === 'saved') toast('Saved the picture. Post it from your photos.');
          } catch (err) { toast((err as Error).message); }
          b.removeAttribute('disabled');
          break;
        }
        case 'reopen': void act({ action: 'reopen' }); break;
        case 'remind': if (p) remindSheet(s, c, p); break;
        case 'person': if (p) personSheet(s, c, p); break;
        // Paying back the difference squares them at their new share.
        case 'paidBack': if (p) void act({ action: 'setPaid', personId: p.id, paid: true }); break;
      }
    };
  }

  function remindSheet(s: PublicSplit, c: Totals, p: PublicPerson): void {
    const r0 = c.people[p.id];
    const from = knownVenue(s) ? `from ${s.venue}` : 'of the bill';
    const text = r0.paid && r0.due > 0
      ? `Hey ${p.name}! Your share ${from} went up ${fmt(r0.due)} after the last items were sorted out. Here's the breakdown and a one-tap pay button: ${friendLink()}`
      : `Hey ${p.name}! Your share ${from} is ${fmt(r0.owed)}. ${s.even ? "We split it evenly. It's all here" : "Everything's itemized here"}, with Venmo and Cash App buttons: ${friendLink()}`;
    const savedPhone = current().phones?.[p.id] || '';
    openSheet(`
      <div style="display:flex;justify-content:space-between;align-items:center"><h2 class="h" style="font-size:26px">Remind ${esc(p.name)}</h2><button class="link" data-close>Cancel</button></div>
      <p class="lede" style="font-size:15px">This opens Messages on your phone with the text ready. It comes from you, so it lands like a normal text.</p>
      <div class="field">
        <label for="remindTo">To ${esc(p.name)}</label>
        <input id="remindTo" class="input" type="tel" inputmode="tel" value="${esc(savedPhone ? prettyPhone(savedPhone) : '')}" placeholder="Phone number, or pick them in Messages">
        ${savedPhone ? '' : `<span class="hint">Type ${esc(p.name)}'s number to save it on this split, or leave it blank and choose them in Messages.</span>`}
        <span class="hint">Only you see saved numbers. We never sell them. ${privacyLink()}</span>
      </div>
      <textarea id="remindText" class="input" aria-label="Reminder text">${esc(text)}</textarea>
      <a class="btn" id="smsLink" href="${smsHref(savedPhone, text)}">Open Messages</a>
      <button class="btn2" data-act="copyText">Copy text instead</button>`,
    (e) => {
      if (target(e, '[data-act=copyText]')) void copy(inputValue('remindText'));
      if (target(e, '#smsLink')) {
        const typed = inputValue('remindTo');
        if (typed.replace(/\D/g, '') !== savedPhone.replace(/\D/g, '')) void act({ action: 'setPhone', personId: p.id, phone: typed });
        setTimeout(closeSheet, 400);
      }
    },
    () => {
      const link = document.getElementById('smsLink') as HTMLAnchorElement | null;
      if (link) link.href = smsHref(inputValue('remindTo').replace(/[^\d+]/g, ''), inputValue('remindText'));
    });
  }

  function personSheet(s: PublicSplit, c: Totals, p: PublicPerson): void {
    const r = c.people[p.id];
    const items = r.items.map((i) => leadCents(esc(i.ways > 1 ? `${i.name} · ${frac(i.ways)}` : i.name), i.cents)).join('');
    openSheet(`
      <h2 class="h" style="font-size:26px">${esc(p.name)}</h2>
      <div class="ledger">
        ${items}
        ${c.even ? leadCents(`Food &amp; drinks · 1 of ${c.even.ways}`, r.sub) : ''}
        ${r.leftoverSub ? leadCents('Share of unclaimed items', r.leftoverSub) : ''}
        ${leadCents(s.taxLabel || 'Tax', r.tax)}${leadCents('Tip', r.tip)}
        ${r.thanks ? leadCents('Thanks to you', r.thanks, 'accent') : ''}
        ${r.fee ? leadCents(`Part of the ${esc(brand.name)} fee`, r.fee, 'accent') : ''}
        ${leadCents('Their share', r.owed, 'total')}
        ${r.paid ? leadCents('Paid', r.paid.cents) : ''}
        ${r.paid && r.due ? leadCents('Still to pay', r.due, 'warn') : ''}
        ${r.refund ? leadCents('You owe them back', r.refund, 'warn') : ''}
      </div>
      ${nothingToPay(r)
        ? '<p class="sub" style="margin:0">Nothing to pay.</p>'
        : r.paid && r.due
        ? `<button class="btn" data-act="pay">They paid the rest</button>
           <button class="btn2" data-act="remind">Remind ${esc(p.name)}</button>
           <button class="link" data-act="forgive">${r.forgiven ? 'Undo: they still owe me' : "Let the difference go"}</button>
           <button class="link" data-act="unpay">Mark as not paid</button>`
        : r.paid
        ? `${r.refund ? '<button class="btn" data-act="pay">I paid them back</button>' : ''}<button class="btn2" data-act="unpay">Mark as not paid</button>`
        : `<button class="btn" data-act="pay">Mark as paid</button>
           <button class="btn2" data-act="remind">Remind ${esc(p.name)}</button>
           <button class="link" data-act="forgive">${r.forgiven ? 'Undo: they still owe me' : "Let it go. They don't owe me."}</button>`}
      <button class="link" data-close>Close</button>`,
    async (e) => {
      const b = target(e, '[data-act]');
      if (!b) return;
      if (b.dataset.act === 'remind') return remindSheet(s, c, p);
      closeSheet();
      if (b.dataset.act === 'pay') await act({ action: 'setPaid', personId: p.id, paid: true });
      else if (b.dataset.act === 'unpay') await act({ action: 'setPaid', personId: p.id, paid: false });
      else if (b.dataset.act === 'forgive') await act({ action: 'forgive', personId: p.id, forgiven: !r.forgiven });
    });
  }
}

